import React, { useState, useMemo, useEffect } from 'react'
import useSWR from 'swr'
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Container,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Strong,
  Table,
  Text,
} from '@radix-ui/themes'
import { ChevronRightIcon, ExclamationTriangleIcon, Pencil2Icon } from '@radix-ui/react-icons'
import { useNavigate } from 'react-router-dom'

import PublicationFormManager from './PublicationFormManager'

import Vehicle from '@/interfaces/Vehicle'
import { useFormatter } from '@/utils/formats'
import { useDateUtils } from '@/lib/date-fns'
import useBranchesFetcher from '@/hooks/useBranchesFetcher'
import { BranchDto } from '@/interfaces/Branch'
import usePublishQueue from '@/hooks/usePublishQueue'
import PublishQueueModal from '@/components/PublishQueueModal'
import { AppPath } from '@/enums/AppPath'
import { makeUrlPublishedStats, makeUrlVehicleEdit } from '@/hooks/connectionData'
import { customFetch, customPost } from '@/utils/customFetch'
import { useAuth } from '@/hooks/useAuth'
import { SelectField } from '@/components/SelectField'
import { BranchState } from '@/enums/DocStates'
import Branch from '@/interfaces/Branch'
import BulkPublishConfirmationModal from '@/components/BulkPublishConfirmationModal'
import BulkUnpublishConfirmationModal from '@/components/BulkUnpublishConfirmationModal'
import InlinePriceEdit from '@/components/InlinePriceEdit'
import { formatWithThousandSep } from '@/utils/utils'
import {
  activeChaProducts,
  chaDaysLeft,
  chaDaysLeftLabel,
  CHA_PRODUCT_FILTER_OPTIONS,
  matchesChaProductFilter,
} from '@/utils/chaProducts'

type VehiclePayload = Vehicle & {
  consignmentPrice: number
  purchasedPrice: number
  expensesTotal: number
  leadsCount: {
    chileautos?: number
    meli?: number
    yapo?: number
  }
  organization: {
    _id: string
    name: string
  }
}

export const VehiclesPublishedStatsPage = () => {
  const { userData } = useAuth()

  // Current SWR implementation with auto-revalidation disabled
  const { data, error, isLoading, mutate } = useSWR<VehiclePayload[]>(
    makeUrlPublishedStats(userData?.organization?._id || ''),
    customFetch,
    {
      revalidateOnFocus: false, // Disable auto-refresh when window gains focus
      revalidateOnReconnect: false, // Disable auto-refresh on network recovery
      refreshInterval: 0, // Disable polling/interval refreshing
      revalidateIfStale: false, // Don't auto-refresh if data becomes stale
      dedupingInterval: 10000, // Cache requests for 10 seconds to prevent duplicates
    }
  )

  const { formatCurrency, formatNumber } = useFormatter()
  const { formatDistanceToNow } = useDateUtils()
  const nav = useNavigate()

  // UPDATED: Multi-vehicle form state management
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])
  const [vehicleFormData, setVehicleFormData] = useState<Record<string, any>>({})
  const [vehicleBranches, setVehicleBranches] = useState<Record<string, BranchDto | null>>({})
  const [vehicleValidationErrors, setVehicleValidationErrors] = useState<Record<string, Record<string, string>>>({})
  const [unsavedChanges, setUnsavedChanges] = useState<Set<string>>(new Set())

  const { fetchBranchDetail, fetchBranches, loading: branchLoading } = useBranchesFetcher()
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([])

  // Branch data state
  const [organizationBranches, setOrganizationBranches] = useState<Branch[]>([])
  const [branchesLoaded, setBranchesLoaded] = useState<boolean>(false)

  // Filter state management
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const [yearFilter, setYearFilter] = useState<string>('')
  const [branchFilter, setBranchFilter] = useState<string>('')
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [notPublishedPlatformFilter, setNotPublishedPlatformFilter] = useState<string>('')
  // Subfiltro de destacados: sólo aplica con Plataformas = ChileAutos.
  const [chaProductFilter, setChaProductFilter] = useState<string>('')
  const [brandFilter, setBrandFilter] = useState<string>('')
  const [timeInStockFilter, setTimeInStockFilter] = useState<string>('')

  // Publication queue state
  const { queueState, addToQueue, addUnpublishToQueue, processQueue, clearQueue, cancelAutoClose, triggerPageRefresh } =
    usePublishQueue()
  const [isPublishQueueModalOpen, setIsPublishQueueModalOpen] = useState(false)

  // Confirmation modal state
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<'mercadolibre' | 'chileautos' | 'yapo' | 'all'>(
    'mercadolibre'
  )
  const [vehiclesToConfirm, setVehiclesToConfirm] = useState<VehiclePayload[]>([])

  // NEW: Unpublish modal state
  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false)

  // Inline edit notification state
  const [syncNotification, setSyncNotification] = useState<{
    vehicleId: string
    message: string
    platforms: string[]
  } | null>(null)

  // Fetch organization branches at mount
  useEffect(() => {
    const loadBranches = async () => {
      if (userData?.organization?._id && !branchesLoaded) {
        try {
          const branches = await fetchBranches(userData.organization._id, BranchState.Activo)

          if (branches) {
            setOrganizationBranches(branches)
          }
          setBranchesLoaded(true)
        } catch (error) {
          console.warn('Failed to fetch organization branches:', error)
          setBranchesLoaded(true) // Set to true to avoid retrying
        }
      }
    }

    loadBranches()
  }, [userData?.organization?._id, fetchBranches, branchesLoaded])

  // Create branch lookup map for performance
  const branchLookup = useMemo(() => {
    const lookup = new Map<string, string>()
    organizationBranches.forEach((branch) => {
      lookup.set(branch._id, branch.name)
    })

    return lookup
  }, [organizationBranches])

  // Filtered data logic
  const availableYears = useMemo(() => {
    if (!data) return []
    const years = Array.from(new Set(data.map((v) => v.year).filter(Boolean)))

    return years.sort((a, b) => b - a) // Descending order (newest first)
  }, [data])

  const availableBrands = useMemo(() => {
    if (!data) return []
    const brands = Array.from(new Set(data.map((v) => v.brand).filter(Boolean)))

    return brands.sort((a, b) => a.localeCompare(b)) // Alphabetical order
  }, [data])

  // Helper function to check which platforms a vehicle is published on
  const getVehiclePlatforms = (vehicle: VehiclePayload): string[] => {
    const platforms: string[] = []

    if (vehicle.external_meli?.publishedAt) platforms.push('meli')

    if (vehicle.external_chileautos?.publishedAt) platforms.push('chileautos')

    if (vehicle.external_yapo?.publishedAt) platforms.push('yapo')

    return platforms
  }

  // NEW: Helper function to detect published platforms for unpublish
  const detectPublishedPlatforms = (vehicle: VehiclePayload): Array<'mercadolibre' | 'chileautos' | 'yapo'> => {
    const platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'> = []

    if (vehicle.external_meli?.identifier) platforms.push('mercadolibre')

    if (vehicle.external_chileautos?.identifier) platforms.push('chileautos')

    if (vehicle.external_yapo?.publishedAt) platforms.push('yapo')

    return platforms
  }

  // Helper function to calculate days in stock
  const getDaysInStock = (createdAt: Date): number => {
    const now = new Date()
    const created = new Date(createdAt)
    const diffTime = Math.abs(now.getTime() - created.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  // Helper function to check if vehicle matches time in stock filter
  const matchesTimeInStockFilter = (vehicle: VehiclePayload, filter: string): boolean => {
    if (!filter) return true

    const daysInStock = getDaysInStock(vehicle.createdAt)

    switch (filter) {
      case '0-30':
        return daysInStock <= 30
      case '31-60':
        return daysInStock >= 31 && daysInStock <= 60
      case '61-90':
        return daysInStock >= 61 && daysInStock <= 90
      case '91-180':
        return daysInStock >= 91 && daysInStock <= 180
      case '180+':
        return daysInStock > 180
      default:
        return true
    }
  }

  const filteredData = useMemo(() => {
    if (!data) return []

    return data.filter((vehicle) => {
      if (yearFilter && vehicle.year !== parseInt(yearFilter)) return false

      if (branchFilter && vehicle.branch !== branchFilter) return false

      if (brandFilter && vehicle.brand !== brandFilter) return false

      if (platformFilter) {
        const vehiclePlatforms = getVehiclePlatforms(vehicle)

        if (!vehiclePlatforms.includes(platformFilter)) return false
      }

      if (notPublishedPlatformFilter) {
        const vehiclePlatforms = getVehiclePlatforms(vehicle)

        if (vehiclePlatforms.includes(notPublishedPlatformFilter)) return false
      }

      if (timeInStockFilter && !matchesTimeInStockFilter(vehicle, timeInStockFilter)) return false

      if (platformFilter === 'chileautos' && !matchesChaProductFilter(vehicle, chaProductFilter)) return false

      return true
    })
  }, [
    data,
    yearFilter,
    branchFilter,
    brandFilter,
    platformFilter,
    chaProductFilter,
    notPublishedPlatformFilter,
    timeInStockFilter,
  ])

  // NEW: Count published vehicles in selection
  const publishedVehicleCount = useMemo(() => {
    if (!filteredData) return 0

    return filteredData
      .filter((vehicle) => selectedVehicleIds.includes(vehicle._id))
      .filter((vehicle) => detectPublishedPlatforms(vehicle).length > 0).length
  }, [selectedVehicleIds, filteredData, detectPublishedPlatforms])

  // Helper function to get branch name
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return 'Sin nombre'
    const branchName = branchLookup.get(branchId)

    return branchName || 'Sin nombre'
  }

  const handleSelectionChange = (vehicleId: string) => {
    setSelectedVehicleIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(vehicleId)) {
        return prevSelectedIds.filter((id) => id !== vehicleId)
      } else {
        return [...prevSelectedIds, vehicleId]
      }
    })
  }

  const handleSelectAllChange = () => {
    if (selectedVehicleIds.length === filteredData?.length) {
      setSelectedVehicleIds([])
    } else {
      setSelectedVehicleIds(filteredData?.map((v) => v._id) ?? [])
    }
  }

  const handleBulkPublish = async (platform: 'mercadolibre' | 'chileautos' | 'yapo' | 'all' = 'mercadolibre') => {
    if (selectedVehicleIds.length === 0) {
      console.warn('No vehicles selected for bulk publishing.')

      return
    }

    console.log(`Initiating bulk publish confirmation for ${selectedVehicleIds.length} vehicles to ${platform}`)

    // Prepare vehicles for confirmation modal
    const selectedVehicles = filteredData.filter((vehicle) => selectedVehicleIds.includes(vehicle._id))

    // Show confirmation modal first
    setSelectedPlatform(platform)
    setVehiclesToConfirm(selectedVehicles)
    setIsConfirmationModalOpen(true)
  }

  const handleConfirmPublish = async () => {
    console.log(`Proceeding with bulk publish for ${vehiclesToConfirm.length} vehicles to ${selectedPlatform}`)

    // Close confirmation modal
    setIsConfirmationModalOpen(false)

    // Show publishing queue modal
    setIsPublishQueueModalOpen(true)

    try {
      // Simple approach: treat "all" the same as individual platforms
      // Let the queue processor handle the platform-specific logic
      const vehiclesToPublish = await Promise.all(
        selectedVehicleIds.map(async (vehicleId) => {
          const vehicle = data?.find((v) => v._id === vehicleId)

          if (!vehicle) {
            console.warn(`Vehicle not found for ID: ${vehicleId}`)

            return null
          }

          let branchData = null

          // Fetch branch details if vehicle has a branch ID
          if (vehicle.branch && typeof vehicle.branch === 'string') {
            try {
              branchData = await fetchBranchDetail(vehicle.branch)
              console.log(`Fetched branch data for vehicle ${vehicle.plate}:`, branchData?.name)
            } catch (error) {
              console.warn(`Failed to fetch branch for vehicle ${vehicle.plate}:`, error)
            }
          } else {
            console.warn(`Vehicle ${vehicle.plate} has no branch ID assigned`)
          }

          // Get user form data if available, otherwise let individual platform logic handle defaults
          let validatedFormData = vehicleFormData[vehicleId]

          // If no user form data, let the individual platform functions handle their own data preparation
          if (!validatedFormData || Object.keys(validatedFormData).length === 0) {
            validatedFormData = undefined // Let individual logic handle it
          }

          return { vehicle, branch: branchData, formData: validatedFormData }
        })
      )

      // Filter out any null results
      const validVehicles = vehiclesToPublish.filter((item) => item !== null)

      if (validVehicles.length === 0) {
        console.error('No valid vehicles to publish')
        setIsPublishQueueModalOpen(false)

        return
      }

      console.log(`Successfully prepared ${validVehicles.length} vehicles for publishing to ${selectedPlatform}`)

      // Add vehicles to queue
      addToQueue(
        validVehicles.map((item) => ({
          vehicle: item.vehicle,
          branch: item.branch,
          formData: item.formData,
          platform: selectedPlatform,
        }))
      )

      // Start processing the queue
      await processQueue()
    } catch (error) {
      console.error('Error preparing vehicles for bulk publish:', error)
      setIsPublishQueueModalOpen(false)
    }
  }

  const handleConfirmationModalClose = () => {
    setIsConfirmationModalOpen(false)
    setVehiclesToConfirm([])
    setSelectedPlatform('mercadolibre')
  }

  // NEW: Handle bulk unpublish
  const handleBulkUnpublish = async () => {
    if (selectedVehicleIds.length === 0) {
      console.warn('No vehicles selected for bulk unpublishing.')

      return
    }

    console.log(`Initiating bulk unpublish for ${selectedVehicleIds.length} vehicles`)

    // Filter to only vehicles that are actually published
    const selectedVehicles = filteredData.filter((vehicle) => selectedVehicleIds.includes(vehicle._id))
    const publishedVehicles = selectedVehicles.filter((vehicle) => detectPublishedPlatforms(vehicle).length > 0)

    if (publishedVehicles.length === 0) {
      console.warn('No selected vehicles are currently published on any platform.')

      return
    }

    setIsUnpublishModalOpen(true)
  }

  // NEW: Handle unpublish confirmation
  const handleUnpublishConfirm = async (platforms: Array<'mercadolibre' | 'chileautos' | 'yapo'>) => {
    const selectedVehicles = filteredData.filter((vehicle) => selectedVehicleIds.includes(vehicle._id))
    const publishedVehicles = selectedVehicles.filter((vehicle) => detectPublishedPlatforms(vehicle).length > 0)

    if (publishedVehicles.length === 0) {
      console.warn('No vehicles to unpublish.')

      return
    }

    console.log(`Processing unpublish for ${publishedVehicles.length} vehicles from platforms:`, platforms)

    // Prepare vehicles for unpublish queue
    const vehiclesWithPlatforms = publishedVehicles
      .map((vehicle) => ({
        vehicle,
        platforms: platforms.filter((platform) => detectPublishedPlatforms(vehicle).includes(platform)),
      }))
      .filter(({ platforms }) => platforms.length > 0)

    if (vehiclesWithPlatforms.length === 0) {
      console.warn('No vehicles match the selected platforms.')

      return
    }

    // Add to unpublish queue
    addUnpublishToQueue(vehiclesWithPlatforms)

    // Close unpublish confirmation modal and open progress modal
    setIsUnpublishModalOpen(false)
    setIsPublishQueueModalOpen(true)

    // Start processing the queue
    await processQueue()
  }

  const handleQueueModalClose = () => {
    // Check if queue is completed and trigger refresh if needed
    const isCompleted = queueState.completionState === 'completed'
    const hasSuccessfulPublications = queueState.completedCount > 0

    setIsPublishQueueModalOpen(false)
    setSelectedVehicleIds([])
    clearQueue()

    // Refresh page if there were successful publications
    if (isCompleted && hasSuccessfulPublications) {
      console.log('Publications completed successfully - refreshing page to show updated statuses')
      setTimeout(() => {
        window.location.reload()
      }, 100)
    }
  }

  const handleToggleRow = async (vehicle: VehiclePayload) => {
    const currentExpandedId = expandedRowIds.find((id) => id === vehicle._id)

    // Collapse if it's already open
    if (currentExpandedId) {
      setExpandedRowIds((prev) => prev.filter((id) => id !== vehicle._id))
      setVehicleBranches((prev) => ({ ...prev, [vehicle._id]: null }))
    } else {
      // Expand the row - PublicationFormManager will handle fetch and null branch
      setExpandedRowIds((prev) => [...prev, vehicle._id])

      // Try to fetch branch details if branch ID exists
      if (vehicle.branch && typeof vehicle.branch === 'string') {
        try {
          const branchDetails = await fetchBranchDetail(vehicle.branch)
          setVehicleBranches((prev) => ({ ...prev, [vehicle._id]: branchDetails }))
        } catch (error) {
          console.warn('Failed to fetch branch details:', error)
          setVehicleBranches((prev) => ({ ...prev, [vehicle._id]: null }))
        }
      } else {
        // No branch ID - set to null and let user manually select branch info
        console.info('Vehicle has no branch ID assigned. Form will open with empty branch fields.', {
          vehicleId: vehicle._id,
          plate: vehicle.plate,
        })
        setVehicleBranches((prev) => ({ ...prev, [vehicle._id]: null }))
      }
    }
  }

  // NEW: Handlers for multi-vehicle form state management
  const handleVehicleFormDataChange = (vehicleId: string, data: any) => {
    setVehicleFormData((prev) => ({ ...prev, [vehicleId]: data }))
    setUnsavedChanges((prev) => new Set([...prev, vehicleId]))
  }

  const handleVehicleValidationErrorsChange = (vehicleId: string, errors: Record<string, string>) => {
    setVehicleValidationErrors((prev) => ({ ...prev, [vehicleId]: errors }))
  }

  // NEW: Utility functions for form status checking
  const getVehicleFormStatus = (vehicleId: string) => {
    const hasUnsavedChanges = unsavedChanges.has(vehicleId)
    const hasValidationErrors =
      vehicleValidationErrors[vehicleId] && Object.keys(vehicleValidationErrors[vehicleId]).length > 0
    const hasFormData = vehicleFormData[vehicleId] && Object.keys(vehicleFormData[vehicleId]).length > 0

    if (hasValidationErrors) return 'error'

    if (hasUnsavedChanges || hasFormData) return 'modified'

    return 'default'
  }

  const getStatusBadge = (vehicleId: string) => {
    const status = getVehicleFormStatus(vehicleId)

    switch (status) {
      case 'error':
        return (
          <Badge size="1" color="red">
            Errores
          </Badge>
        )
      case 'modified':
        return (
          <Badge size="1" color="yellow">
            Editado
          </Badge>
        )
      default:
        return null
    }
  }

  // NEW: Handle auto-close completion
  const handleAutoCloseComplete = () => {
    console.log('Auto-close triggered - closing modal and refreshing page')
    setIsPublishQueueModalOpen(false)
    setSelectedVehicleIds([])
    clearQueue()
    // Trigger full page refresh after 100ms delay to ensure modal closes smoothly
    setTimeout(() => {
      triggerPageRefresh()
    }, 100)
  }

  // NEW: Refresh specific vehicle data after image upload
  const refreshVehicleData = async (vehicleId: string) => {
    try {
      console.log('Refreshing vehicle data after image upload:', vehicleId)
      // Trigger SWR revalidation to get fresh data from server
      await mutate()
      console.log('Vehicle data refreshed successfully')
    } catch (error) {
      console.error('Failed to refresh vehicle data:', error)
    }
  }

  // NEW: Handle inline price editing
  // This function leverages the existing /api/vehicle_edit endpoint to update prices
  // and automatically sync changes to external platforms (MercadoLibre, ChileAutos, Yapo)
  const handleInlinePriceSave = async (
    vehicleId: string,
    newPrice: number
  ): Promise<{ success: boolean; message?: string; syncInfo?: { platforms: string[] } }> => {
    try {
      // Find the vehicle to update
      const vehicle = data?.find((v) => v._id === vehicleId)

      if (!vehicle) {
        throw new Error('Vehicle not found')
      }

      // Create updated vehicle payload (extract only necessary fields for backend)
      const updatedVehicle = {
        _id: vehicle._id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version,
        year: vehicle.year,
        type: vehicle.type,
        body: vehicle.body,
        color: vehicle.color,
        odometer: vehicle.odometer,
        gearbox: vehicle.gearbox,
        fuel: vehicle.fuel,
        plate: vehicle.plate,
        chassisId: vehicle.chassisId,
        engineId: vehicle.engineId,
        owners: vehicle.owners,
        keys: vehicle.keys,
        branch: vehicle.branch, // This should already be just the ID string
        state: vehicle.state,
        appraisal: vehicle.appraisal,
        price: newPrice, // Updated price
        suggestedPrice: vehicle.suggestedPrice,
        documents: vehicle.documents,
        maintenance: vehicle.maintenance,
        comments: vehicle.comments,
        characteristics: vehicle.characteristics,
        expenses: vehicle.expenses || [],
        incomes: vehicle.incomes || [],
        pictures: vehicle.pictures,
        videoUrl: vehicle.videoUrl,
        // Extract just the organization ID instead of the full object
        organization:
          typeof vehicle.organization === 'object' && vehicle.organization !== null && '_id' in vehicle.organization
            ? (vehicle.organization as any)._id
            : vehicle.organization,
        // Add profile if it exists (may not be in publish data)
        profile: vehicle.profile || vehicle.publishedBy,
        price_format: formatWithThousandSep(newPrice),
      }

      console.log(`Updating vehicle ${vehicle.plate} price from ${vehicle.price} to ${newPrice}`)

      // Call existing vehicle edit API endpoint using established URL maker pattern
      const response = await customPost<{
        message: string
        meliSyncTriggered: boolean
        chaSyncTriggered: boolean
        yapoSyncTriggered: boolean
        changedFields: string[]
      }>(makeUrlVehicleEdit(true), updatedVehicle)

      // Determine which platforms were synced
      const syncedPlatforms = []

      if (response.meliSyncTriggered) syncedPlatforms.push('MercadoLibre')

      if (response.chaSyncTriggered) syncedPlatforms.push('ChileAutos')

      if (response.yapoSyncTriggered) syncedPlatforms.push('Yapo')

      // Show sync notification if platforms were triggered
      if (syncedPlatforms.length > 0) {
        setSyncNotification({
          vehicleId,
          message: `Precio actualizado. Sincronizando con: ${syncedPlatforms.join(', ')}`,
          platforms: syncedPlatforms,
        })

        // Clear notification after 5 seconds
        setTimeout(() => {
          setSyncNotification(null)
        }, 5000)

        console.log(`Price updated for ${vehicle.plate}. Syncing with: ${syncedPlatforms.join(', ')}`)
      } else {
        console.log(`Price updated for ${vehicle.plate}. No external platform sync required.`)
      }

      // Refresh table data to show updated price
      await mutate()

      return {
        success: true,
        message: response.message,
        syncInfo: { platforms: syncedPlatforms },
      }
    } catch (error) {
      console.error('Error updating vehicle price:', error)

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error desconocido al actualizar precio',
      }
    }
  }

  return (
    <Container py="4" maxWidth="100%" px="6" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <div
        className="space-y-4 pr-6 max-w-full overflow-hidden"
        style={{ maxWidth: '100%', overflow: 'hidden', width: '100%' }}
      >
        <div>
          <Heading as="h1">Vehículos Publicados</Heading>
          <Heading as="h2" size="3" mb="2" color="gray">
            Plataformas Externas
          </Heading>
        </div>

        {/* Sync Notification */}
        {syncNotification && (
          <Callout.Root color="blue" className="mb-4">
            <Callout.Text>
              <Strong>Sincronización en curso:</Strong> {syncNotification.message}
            </Callout.Text>
          </Callout.Root>
        )}

        {/* Filters Section */}
        <div className="mb-4">
          <div className="flex justify-center mb-2">
            <button
              className="text-blue-500 font-bold"
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Toggle filters"
            >
              {showFilters ? 'Esconder filtros de búsqueda' : 'Ver filtros de búsqueda'}
            </button>
          </div>

          {showFilters && (
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${
                platformFilter === 'chileautos' ? 'xl:grid-cols-8' : 'xl:grid-cols-7'
              } items-end gap-x-4 gap-y-2 max-w-7xl mx-auto`}
            >
              <SelectField
                label="Año"
                name="year"
                value={yearFilter}
                options={availableYears.reduce((acc, year) => ({ ...acc, [year]: year.toString() }), {})}
                handleInputChange={(e) => setYearFilter(e.target.value)}
                firstOptionText="-- Todos los años --"
              />
              <SelectField
                label="Sucursal"
                name="branch"
                value={branchFilter}
                options={organizationBranches.reduce((acc, branch) => ({ ...acc, [branch._id]: branch.name }), {})}
                handleInputChange={(e) => setBranchFilter(e.target.value)}
                firstOptionText="-- Todas las sucursales --"
              />
              <SelectField
                label="Marca"
                name="brand"
                value={brandFilter}
                options={availableBrands.reduce((acc, brand) => ({ ...acc, [brand]: brand }), {})}
                handleInputChange={(e) => setBrandFilter(e.target.value)}
                firstOptionText="-- Todas las marcas --"
              />
              <SelectField
                label="Plataformas"
                name="platform"
                value={platformFilter}
                options={{
                  meli: 'MercadoLibre',
                  chileautos: 'ChileAutos',
                  yapo: 'Yapo',
                }}
                handleInputChange={(e) => {
                  setPlatformFilter(e.target.value)

                  // Al salir de ChileAutos el subfiltro deja de aplicar; se limpia para
                  // que no quede activo y oculto si se vuelve a elegir.
                  if (e.target.value !== 'chileautos') setChaProductFilter('')
                }}
                firstOptionText="-- Todas las plataformas --"
              />
              {platformFilter === 'chileautos' && (
                <SelectField
                  label="Destacado"
                  name="chaProduct"
                  value={chaProductFilter}
                  options={CHA_PRODUCT_FILTER_OPTIONS}
                  handleInputChange={(e) => setChaProductFilter(e.target.value)}
                  firstOptionText="-- Todos --"
                />
              )}
              <SelectField
                label="No publicado en"
                name="notPublishedPlatform"
                value={notPublishedPlatformFilter}
                options={{
                  meli: 'MercadoLibre',
                  chileautos: 'ChileAutos',
                  yapo: 'Yapo',
                }}
                handleInputChange={(e) => setNotPublishedPlatformFilter(e.target.value)}
                firstOptionText="-- Todas las plataformas --"
              />
              <SelectField
                label="Tiempo en Publicación"
                name="timeInStock"
                value={timeInStockFilter}
                options={{
                  '0-30': '0-30 días',
                  '31-60': '31-60 días',
                  '61-90': '61-90 días',
                  '91-180': '91-180 días',
                  '180+': 'Más de 180 días',
                }}
                handleInputChange={(e) => setTimeInStockFilter(e.target.value)}
                firstOptionText="-- Todos los períodos --"
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setYearFilter('')
                    setBranchFilter('')
                    setBrandFilter('')
                    setPlatformFilter('')
                    setChaProductFilter('')
                    setNotPublishedPlatformFilter('')
                    setTimeInStockFilter('')
                  }}
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          )}
        </div>

        {isLoading && (
          <Flex direction="column" align="center" justify="center" gap="2" p="6">
            <Spinner size="3" />
            <Text size="2">Cargando...</Text>
          </Flex>
        )}
        {error && (
          <Callout.Root color="red">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              <Strong>Error:</Strong> {error.message}
            </Callout.Text>
          </Callout.Root>
        )}
        {data && (
          <>
            <div
              className="overflow-x-auto mr-6 max-w-full"
              style={{ maxWidth: '100%', overflow: 'hidden', width: '100%' }}
            >
              <Table.Root
                variant="surface"
                className="w-full table-fixed"
                style={{ maxWidth: '100%', tableLayout: 'fixed', width: '100%', overflow: 'hidden' }}
              >
                <Table.Header>
                  <Table.Row align="center">
                    <Table.ColumnHeaderCell>
                      <Checkbox
                        checked={
                          selectedVehicleIds.length === filteredData.length
                            ? true
                            : selectedVehicleIds.length > 0
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={handleSelectAllChange}
                        aria-label="Seleccionar todo"
                      />
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>
                      <Flex gap="2">
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger>
                            <Button disabled={selectedVehicleIds.length === 0} size="3" className="whitespace-nowrap">
                              Publicar ({selectedVehicleIds.length}) ▼
                            </Button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content>
                            <DropdownMenu.Item onClick={() => handleBulkPublish('mercadolibre')}>
                              MercadoLibre
                            </DropdownMenu.Item>
                            <DropdownMenu.Item onClick={() => handleBulkPublish('chileautos')}>
                              ChileAutos
                            </DropdownMenu.Item>
                            <DropdownMenu.Item onClick={() => handleBulkPublish('yapo')}>Yapo</DropdownMenu.Item>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Item onClick={() => handleBulkPublish('all')}>
                              Todas las Plataformas
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>

                        {/* NEW: Unpublish button */}
                        {publishedVehicleCount > 0 && (
                          <Button
                            onClick={handleBulkUnpublish}
                            disabled={publishedVehicleCount === 0}
                            size="3"
                            color="red"
                            className="whitespace-nowrap"
                          >
                            ⏸ Despublicar ({publishedVehicleCount})
                          </Button>
                        )}
                      </Flex>
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: '3%', maxWidth: '50px' }}>Patente</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: '8%', maxWidth: '100px' }}>Vehículo</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="Año" align="center" style={{ width: '4%', maxWidth: '60px' }}>
                      Año
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="Sucursal" align="center" style={{ width: '6%', maxWidth: '80px' }}>
                      Sucursal
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="KMs" align="right" style={{ width: '3%', maxWidth: '40px' }}>
                      KMs
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="Costo Total" align="right" style={{ width: '5%', maxWidth: '70px' }}>
                      Costo Total
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell
                      title="Precio Sugerido"
                      align="right"
                      style={{ width: '7%', maxWidth: '90px' }}
                    >
                      Precio Sugerido
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell
                      title="Precio Publicación"
                      align="right"
                      style={{ width: '7%', maxWidth: '90px' }}
                    >
                      Precio Publicación
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="Tiempo en Stock" style={{ width: '5%', maxWidth: '70px' }}>
                      Tiempo en Stock
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell style={{ width: '10%', maxWidth: '100px' }}>
                      Plataformas
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell title="Acciones" style={{ width: '3%', maxWidth: '50px' }}>
                      Acciones
                    </Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredData.map((vehicle) => {
                    const totalCost =
                      (vehicle.purchasedPrice ? (vehicle.purchasedPrice ?? 0) : (vehicle.consignmentPrice ?? 0)) +
                      vehicle.expensesTotal

                    const totalLeads =
                      ((vehicle.external_chileautos?.publishedAt && vehicle.leadsCount.chileautos) || 0) +
                      ((vehicle.external_yapo?.publishedAt && vehicle.leadsCount.yapo) || 0) +
                      ((vehicle.external_meli?.publishedAt && vehicle.leadsCount.meli) || 0)

                    return (
                      <React.Fragment key={vehicle._id}>
                        <Table.Row>
                          <Table.Cell>
                            <Checkbox
                              checked={selectedVehicleIds.includes(vehicle._id)}
                              onCheckedChange={() => handleSelectionChange(vehicle._id)}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <IconButton
                              variant="ghost"
                              onClick={() => handleToggleRow(vehicle)}
                              aria-label={expandedRowIds.includes(vehicle._id) ? 'Cerrar' : 'Editar'}
                            >
                              <Pencil2Icon />
                            </IconButton>
                          </Table.Cell>
                          <Table.Cell aria-label="plate">
                            <Flex align="center" gap="2">
                              <Strong>{vehicle.plate}</Strong>
                              {getStatusBadge(vehicle._id)}
                            </Flex>
                          </Table.Cell>
                          <Table.Cell aria-label="vehicle">
                            <Text as="p">
                              {vehicle.brand} {vehicle.model}
                            </Text>
                            <Text color="gray" size="1">
                              {vehicle.version}
                            </Text>
                          </Table.Cell>
                          <Table.Cell align="center" aria-label="year">
                            <Text size="2">{vehicle.year || '-'}</Text>
                          </Table.Cell>
                          <Table.Cell align="center" aria-label="branch">
                            <Text size="2">{getBranchName(vehicle.branch)}</Text>
                          </Table.Cell>
                          <Table.Cell align="right" aria-label="odometer">
                            {formatNumber(vehicle.odometer, '-')}
                          </Table.Cell>
                          <Table.Cell align="right" aria-label="total-cost">
                            {formatCurrency(totalCost, '-')}
                          </Table.Cell>
                          <Table.Cell align="right" aria-label="suggested-price">
                            {formatCurrency(vehicle.suggestedPrice, '-')}
                          </Table.Cell>
                          <Table.Cell align="right" aria-label="published-price">
                            <InlinePriceEdit
                              vehicleId={vehicle._id}
                              currentPrice={vehicle.price}
                              onSave={handleInlinePriceSave}
                              disabled={false}
                            />
                          </Table.Cell>
                          <Table.Cell align="right" aria-label="stock-time">
                            {formatDistanceToNow(new Date(vehicle.createdAt))}
                          </Table.Cell>
                          <Table.Cell>
                            <Flex gap="2" wrap="wrap">
                              {vehicle.publishedAt && (
                                <Badge size="1" color="red">
                                  Veekls Market
                                </Badge>
                              )}

                              {vehicle.external_chileautos?.publishedAt && (
                                <Flex wrap="nowrap" gap="1">
                                  <Badge size="1" color="blue">
                                    Chileautos
                                  </Badge>
                                  {vehicle.leadsCount.chileautos && (
                                    <Badge size="1" color="blue" variant="solid">
                                      {vehicle.leadsCount.chileautos}
                                    </Badge>
                                  )}
                                  {/* Destacados aplicados desde Veekls; los del portal de ChileAutos no se ven. */}
                                  {activeChaProducts(vehicle).map((product) => (
                                    <Badge
                                      key={`${product.code}-${product.appliedAt}`}
                                      size="1"
                                      color="amber"
                                      title={chaDaysLeftLabel(chaDaysLeft(product))}
                                    >
                                      ★ {product.name}
                                    </Badge>
                                  ))}
                                </Flex>
                              )}

                              {vehicle.external_meli?.publishedAt && (
                                <Flex wrap="nowrap" gap="1">
                                  <Badge size="1" color="yellow">
                                    MercadoLibre
                                  </Badge>
                                  {vehicle.leadsCount.meli && (
                                    <Badge size="1" color="yellow" variant="solid">
                                      {vehicle.leadsCount.meli}
                                    </Badge>
                                  )}
                                </Flex>
                              )}

                              {vehicle.external_yapo?.publishedAt && (
                                <Flex wrap="nowrap" gap="1">
                                  <Badge size="1" color="orange">
                                    Yapo
                                  </Badge>
                                  {vehicle.leadsCount.yapo && (
                                    <Badge size="1" color="orange" variant="solid">
                                      {vehicle.leadsCount.yapo}
                                    </Badge>
                                  )}
                                </Flex>
                              )}

                              <Flex wrap="nowrap" gap="1">
                                <Badge size="1" color="gray">
                                  Total
                                </Badge>
                                <Badge size="1" color="gray" variant="solid">
                                  {totalLeads}
                                </Badge>
                              </Flex>
                            </Flex>
                          </Table.Cell>
                          <Table.Cell align="right">
                            <Button variant="ghost" onClick={() => nav(`/${AppPath.Vehicles}/view/${vehicle._id}`)}>
                              Ver <ChevronRightIcon />
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                        {expandedRowIds.includes(vehicle._id) && (
                          <Table.Row>
                            <Table.Cell colSpan={14} className="p-0">
                              {branchLoading ? (
                                <Flex direction="column" align="center" justify="center" gap="2" p="6">
                                  <Spinner size="3" />
                                  <Text size="2">Cargando datos de la sucursal...</Text>
                                </Flex>
                              ) : (
                                <PublicationFormManager
                                  vehicle={vehicle}
                                  branch={vehicleBranches[vehicle._id] ?? null}
                                  onPublishSuccess={async () => {
                                    // Refresh data and close form after publishing
                                    await refreshVehicleData(vehicle._id)
                                    setExpandedRowIds((prev) => prev.filter((id) => id !== vehicle._id))
                                    setUnsavedChanges((prev) => {
                                      const newSet = new Set(prev)
                                      newSet.delete(vehicle._id)

                                      return newSet
                                    })
                                  }}
                                  onImageUpload={async (vehicleId: string) => {
                                    // Refresh data but keep form open after image upload
                                    await refreshVehicleData(vehicleId)
                                  }}
                                  onVideoUpload={async (vehicleId: string) => {
                                    // Refresh data but keep form open after video upload
                                    await refreshVehicleData(vehicleId)
                                  }}
                                  formData={vehicleFormData[vehicle._id]}
                                  onFormDataChange={handleVehicleFormDataChange}
                                  validationErrors={vehicleValidationErrors[vehicle._id]}
                                  onValidationErrorsChange={handleVehicleValidationErrorsChange}
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </React.Fragment>
                    )
                  })}
                </Table.Body>
              </Table.Root>
            </div>
          </>
        )}
        {/* Confirmation Modal */}
        <BulkPublishConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={handleConfirmationModalClose}
          onConfirm={handleConfirmPublish}
          selectedVehicles={vehiclesToConfirm}
          platform={selectedPlatform}
          getBranchName={getBranchName}
        />

        {/* NEW: Unpublish Confirmation Modal */}
        <BulkUnpublishConfirmationModal
          isOpen={isUnpublishModalOpen}
          onClose={() => setIsUnpublishModalOpen(false)}
          onConfirm={handleUnpublishConfirm}
          selectedVehicles={filteredData.filter((vehicle) => selectedVehicleIds.includes(vehicle._id))}
          getBranchName={getBranchName}
        />

        {/* Publication Queue Modal */}
        <PublishQueueModal
          isOpen={isPublishQueueModalOpen}
          onClose={handleQueueModalClose}
          queueState={queueState}
          onCancel={clearQueue}
          onCancelAutoClose={cancelAutoClose}
          onAutoCloseComplete={handleAutoCloseComplete}
          operation={queueState.items.length > 0 ? queueState.items[0].operation : 'publish'}
        />
      </div>
    </Container>
  )
}
